import { useState } from 'react';
import { Button, DatePicker, Form, Input, Select, Space, Tooltip } from 'antd';

import useLocale from '../../../hooks/useLocale';
import type { ClientService } from '../../../shared/api/ClientService';
import StyledTable from '../components/StyledTable/StyledTable';
import EventDetailModal from './EventDetailModal';
import LoadErrorAlert from './LoadErrorAlert';
import ReplayEventsModal from './ReplayEventsModal';
import { getEventColumns } from './eventColumns';
import {
  DEFAULT_EVENT_FILTERS,
  DEFAULT_EVENT_SORT,
  EVENT_COLUMN_WIDTH,
  EVENT_FILTER_IDS,
  EVENT_STATUS_FILTER_ORDER,
  EVENT_TABLE_SCROLL_X,
} from './constants';
import type { EventStatus, IEventFilters, IEventSort } from './types';
import { useIntegrationEvents } from './useIntegrationMonitoring';
import { useLeaveEmptyPage, useListControls } from './useListControls';
import { buildEventListParams, formatText, getEventStatusLabel, getIds, toDateRange, toEventSortState } from './utils';

type IntegrationEvent = ClientService.IntegrationEventDto;

interface IProps {
  isIntegrationEnabled: boolean | undefined;
}

const NO_EVENTS: IntegrationEvent[] = [];

// StyledTable is not generic, so the row type is kept on these helpers.
const getRowKey = ({ id }: IntegrationEvent): string => id ?? '';
const getCheckboxProps = ({ canBeReapplied }: IntegrationEvent) => ({ disabled: !canBeReapplied });

const EventsTab = ({ isIntegrationEnabled }: IProps): JSX.Element => {
  const { t } = useLocale();
  const controls = useListControls<IEventFilters, IEventSort>({
    defaultFilters: DEFAULT_EVENT_FILTERS,
    defaultSort: DEFAULT_EVENT_SORT,
    toSort: toEventSortState,
  });
  const { filters, sort, page, pageSize, selectedIds, setSelectedIds, changeFilters } = controls;
  const [eventTypeDraft, setEventTypeDraft] = useState('');
  const [detailEvent, setDetailEvent] = useState<IntegrationEvent>();
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  // Undefined while the replay modal is closed; otherwise the ids it starts with (none when opened by id).
  const [replayIds, setReplayIds] = useState<string[]>();

  const eventsQuery = useIntegrationEvents(buildEventListParams(filters, sort, page, pageSize));

  const events = eventsQuery.data?.items ?? NO_EVENTS;
  const totalCount = eventsQuery.data?.totalCount ?? 0;
  // Only rows that can still be applied again count: a row a refresh made unavailable must not stay checked.
  const selectedEvents = events.filter(
    ({ id, canBeReapplied }) => id !== undefined && canBeReapplied && selectedIds.includes(id)
  );
  const isPastLastPage =
    eventsQuery.data !== undefined && !eventsQuery.isPreviousData && events.length === 0 && page > 1;

  useLeaveEmptyPage(isPastLastPage, totalCount, pageSize, controls.setPage);

  const changeEventType = (value: string) => {
    setEventTypeDraft(value);
    // Clearing the box has nothing to wait for.
    if (!value && filters.eventType) changeFilters({ eventType: '' });
  };

  const applyEventType = () => {
    const eventType = eventTypeDraft.trim();
    if (eventType !== filters.eventType) changeFilters({ eventType });
  };

  const resetFilters = () => {
    controls.resetFilters();
    setEventTypeDraft('');
  };

  const openDetail = (event: IntegrationEvent) => {
    setDetailEvent(event);
    setIsDetailOpen(true);
  };

  const statusOptions = EVENT_STATUS_FILTER_ORDER.map((status) => ({
    value: status,
    label: getEventStatusLabel(status, t),
  }));

  return (
    <>
      <Form layout="inline" className="integration-monitoring__filters">
        <Form.Item label={t.STATUS} htmlFor={EVENT_FILTER_IDS.status}>
          <Select<EventStatus>
            allowClear
            id={EVENT_FILTER_IDS.status}
            className="integration-monitoring__filter-control"
            placeholder={t.ALL}
            options={statusOptions}
            value={filters.status}
            onChange={(status) => changeFilters({ status })}
          />
        </Form.Item>
        <Form.Item label={t.INTEGRATION_EVENT_TYPE} htmlFor={EVENT_FILTER_IDS.eventType}>
          <Input
            allowClear
            id={EVENT_FILTER_IDS.eventType}
            className="integration-monitoring__filter-control"
            placeholder={t.INTEGRATION_EVENT_TYPE_EXACT}
            value={eventTypeDraft}
            onChange={({ target }) => changeEventType(target.value)}
            onPressEnter={applyEventType}
            onBlur={applyEventType}
          />
        </Form.Item>
        <Form.Item label={t.INTEGRATION_OCCURRED} htmlFor={EVENT_FILTER_IDS.occurred}>
          <DatePicker.RangePicker
            id={EVENT_FILTER_IDS.occurred}
            value={filters.occurredRange}
            onChange={(range) => changeFilters({ occurredRange: toDateRange(range) })}
          />
        </Form.Item>
        <Form.Item>
          <Button onClick={resetFilters}>{t.RESET}</Button>
        </Form.Item>
      </Form>

      <Space className="integration-monitoring__toolbar" wrap>
        <Tooltip title={selectedEvents.length === 0 ? t.INTEGRATION_SELECT_EVENT_FIRST : undefined}>
          <Button disabled={selectedEvents.length === 0} onClick={() => setReplayIds(getIds(selectedEvents))}>
            {formatText(t.INTEGRATION_REPLAY_SELECTED, selectedEvents.length)}
          </Button>
        </Tooltip>
        <Button onClick={() => setReplayIds([])}>{t.INTEGRATION_REPLAY_BY_ID}</Button>
      </Space>

      {eventsQuery.isError && <LoadErrorAlert error={eventsQuery.error} onRetry={() => eventsQuery.refetch()} />}

      <StyledTable
        rowKey={getRowKey}
        columns={getEventColumns({ t, sort, onView: openDetail })}
        dataSource={events}
        loading={eventsQuery.isFetching}
        scroll={{ x: EVENT_TABLE_SCROLL_X }}
        onChange={controls.handleTableChange}
        rowSelection={{
          selectedRowKeys: getIds(selectedEvents),
          columnWidth: EVENT_COLUMN_WIDTH.selection,
          onChange: (keys) => setSelectedIds(keys.map(String)),
          getCheckboxProps,
          renderCell: (_checked, { canBeReapplied }: IntegrationEvent, _index, checkbox) =>
            canBeReapplied ? checkbox : <Tooltip title={t.INTEGRATION_NOT_REPLAYABLE}>{checkbox}</Tooltip>,
        }}
        pagination={controls.getPagination(totalCount)}
      />

      <EventDetailModal event={detailEvent} open={isDetailOpen} onClose={() => setIsDetailOpen(false)} />
      {replayIds && (
        <ReplayEventsModal
          initialIds={replayIds}
          isIntegrationEnabled={isIntegrationEnabled}
          onReplayed={() => setSelectedIds([])}
          onClose={() => setReplayIds(undefined)}
        />
      )}
    </>
  );
};

export default EventsTab;
